import type React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { useCommunicationMode, useSupportEmail } from '@hooks'

import { IntercomLauncherButton } from '@components/Intercom'

import { COMMUNICATION_MODE_HUBSPOT } from '@constants'

import invoiceNotFoundIconSrc from '@assets/img/not_found_icon.svg'

import { Button } from './Buttons'
import { InheritLink } from './Links'
import { Typography } from './Typography'
import { FlexColumn } from './Wrappers'

import { CustomerServiceButtonOrLinkMessage, ReloadPageButtonMessage, UnexpectedErrorMessage } from '@messages'

const Wrapper = styled(FlexColumn)`
  gap: 30px;
  max-width: 730px;
  text-align: center;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
`

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  gap: 20px;

  & button {
    min-width: 110px;
  }

  &:first-child {
    grid-column: 1/2;
  }
  &:last-child {
    grid-column: 1/2;
  }
`

interface CustomErrorBoundaryViewProps {
  eventId?: Nullable<string>
  withSupportChannel?: boolean
}

export function CustomErrorBoundaryView({ eventId, withSupportChannel = false }: CustomErrorBoundaryViewProps) {
  const communicationMode = useCommunicationMode()
  // Error screens address a technical audience: hubspot mode gets the mailto presentation instead
  // of the accountant slide over.
  const isHubspotMode = communicationMode === COMMUNICATION_MODE_HUBSPOT
  const isIntercomMode = withSupportChannel && !isHubspotMode
  const supportEmail = useSupportEmail()

  function handleReloadWindow() {
    window.location.reload()
  }

  return (
    <Wrapper>
      <img role="presentation" alt="" src={invoiceNotFoundIconSrc} />
      <Typography size="heading-1" color="gray-80">
        {UnexpectedErrorMessage}
      </Typography>
      <Typography size="400-md" color="gray-80">
        {isIntercomMode ? (
          <FormattedMessage
            id="views.errorBoundary.withIntercom.text"
            defaultMessage="Sajnáljuk az esetleges kellemetlenségeket. Ezt a hibát automatikusan rögzítettük, kijavításán dolgozunk. Kérjük, próbáld meg az oldal újratöltését. Ha a hiba továbbra is fennáll, fordulj ügyfélszolgálatunkhoz."
          />
        ) : (
          <FormattedMessage
            id="views.errorBoundary.withoutIntercom.text"
            defaultMessage="Sajnáljuk az esetleges kellemetlenségeket. Ezt a hibát automatikusan rögzítettük, kijavításán dolgozunk. Kérjük, próbáld meg az oldal újratöltését. Ha a hiba továbbra is fennáll, <mailto>írj nekünk a {supportEmail} címre</mailto>."
            values={{
              supportEmail,
              mailto: function renderInheritLink(chunks: React.ReactNode[]) {
                return <InheritLink href={`mailto:${supportEmail}`}>{chunks}</InheritLink>
              },
            }}
          />
        )}
      </Typography>
      {eventId && (
        <Typography size="700-sm" color="gray-80">
          <FormattedMessage
            id="views.errorBoundary.errorId"
            defaultMessage="Hibaazonosító: {eventId}"
            values={{ eventId }}
          />
        </Typography>
      )}
      <ButtonsContainer>
        <Button variant="primaryContained" onClick={handleReloadWindow}>
          {ReloadPageButtonMessage}
        </Button>
        {isIntercomMode && (
          <IntercomLauncherButton variant="secondaryContained">
            {CustomerServiceButtonOrLinkMessage}
          </IntercomLauncherButton>
        )}
      </ButtonsContainer>
    </Wrapper>
  )
}
